// First- and last-touch UTM attribution.
//
// On every page load:
//   - If the URL carries any UTM/click-id params, write them into a `last`
//     cookie (overwritten each visit) AND, if not already set, the `first`
//     cookie (write-once until consumed at signup).
//   - At signup, the Rails AttributionCapture concern reads both cookies and
//     persists them to user_attributions, then clears both.
//
// No server beacon / no anonymous tracking — SEO get click data from the ad
// platforms themselves; we only need the per-user attribution at signup.

const FIRST_COOKIE = '_laced_utm_first'
const LAST_COOKIE = '_laced_utm_last'
const COOKIE_TTL_DAYS = 90

const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_id',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid'
] as const

type AttributionKey = (typeof ATTRIBUTION_PARAMS)[number]
type AttributionPayload = Partial<Record<AttributionKey, string>> & {
  landing_page?: string
  landing_referrer?: string
  touched_at?: string
}

const readCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))

  return match ? decodeURIComponent(match[1]) : null
}

const writeCookie = (name: string, value: string, days: number): void => {
  const expires = new Date(Date.now() + days * 86400 * 1000).toUTCString()

  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; expires=${expires}; path=/; SameSite=Lax`
}

const extractAttributionFromUrl = (search: string): AttributionPayload => {
  const params = new URLSearchParams(search)
  const out: AttributionPayload = {}

  ATTRIBUTION_PARAMS.forEach((key) => {
    const value = params.get(key)

    if (value) out[key] = value
  })

  return out
}

export const captureUtm = (): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  try {
    const attribution = extractAttributionFromUrl(window.location.search)

    if (Object.keys(attribution).length === 0) return

    const enriched: AttributionPayload = {
      ...attribution,
      landing_page: window.location.href.slice(0, 2048),
      landing_referrer: (document.referrer || '').slice(0, 2048) || undefined,
      touched_at: new Date().toISOString()
    }

    const serialised = JSON.stringify(enriched)

    // Last-touch always overwrites — refreshes the 90d window each time too.
    writeCookie(LAST_COOKIE, serialised, COOKIE_TTL_DAYS)

    // First-touch: only set if absent. Don't refresh TTL — the 90d clock runs
    // from the user's first ever click, matching the conversion window.
    if (!readCookie(FIRST_COOKIE)) {
      writeCookie(FIRST_COOKIE, serialised, COOKIE_TTL_DAYS)
    }
  } catch {
    // Never let analytics break the page.
  }
}
